import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, AlertTriangle, Check, Save, Wand2 } from 'lucide-react';

/**
 * AIRewriteModal
 *
 * The "Generate AI rewrite" review dialog from `ArtworkManager`. Pure
 * presentation — parent owns `rewriteState` and the three handlers
 * (`onClose`, `onSave`, `onRegenerate`). Extracted to bring
 * `ArtworkManager.jsx` under 750 lines.
 */
const AIRewriteModal = ({
  rewriteState,
  onClose,
  onSave,
  onRegenerate,
  onDraftChange,
}) => (
  <AnimatePresence>
    {rewriteState && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        onClick={() => !rewriteState.loading && onClose()}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
          data-testid="rewrite-modal"
        >
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">AI rewrite</p>
              <h2 className="font-serif text-2xl truncate">{rewriteState.artwork.title}</h2>
              <p className="text-sm text-gray-600 truncate">{rewriteState.artwork.artistName}</p>
            </div>
            <button
              onClick={onClose}
              disabled={rewriteState.loading}
              className="p-2 hover:bg-gray-100 rounded"
              data-testid="rewrite-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {rewriteState.loading && (
              <div className="py-16 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600">
                  Generating a fact-locked draft from the verified catalogue record…
                </p>
                <p className="text-xs text-gray-500 mt-2">~10–15 seconds</p>
              </div>
            )}

            {rewriteState.error && (
              <div className="p-4 rounded bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {rewriteState.error}
              </div>
            )}

            {!rewriteState.loading && !rewriteState.error && rewriteState.draft && (
              <>
                {/* Validation banner */}
                {rewriteState.validation?.passes ? (
                  <div className="p-3 rounded bg-green-50 border border-green-200 flex items-center gap-2 text-sm text-green-800">
                    <Check className="w-4 h-4" />
                    <span><strong>{rewriteState.wordCount} words</strong> · Passed fact-checks (artist + title verbatim, no hallucinated exhibitions/prices/awards)</span>
                  </div>
                ) : (
                  <div className="p-3 rounded bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <strong>Validation flagged some issues — review carefully:</strong>
                      <ul className="mt-1 ml-4 list-disc">
                        {rewriteState.validation.issues.map((issue) => (
                          <li key={`issue-${issue}`}>{issue}</li>
                        ))}
                        {rewriteState.validation.flagged_phrases.map((p) => (
                          <li key={`phrase-${p}`}>Possible hallucination — phrase &ldquo;{p}&rdquo; not in fact sheet</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Original (collapsed) */}
                {rewriteState.previous && (
                  <details className="border border-gray-200 rounded p-3 bg-gray-50">
                    <summary className="cursor-pointer text-xs uppercase tracking-[0.2em] text-gray-500">
                      Original description ({rewriteState.previous.length} chars) — click to view
                    </summary>
                    <p className="mt-3 text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {rewriteState.previous}
                    </p>
                  </details>
                )}

                {/* Editable draft */}
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                    New draft — edit freely before saving
                  </p>
                  <textarea
                    value={rewriteState.editedDraft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    rows={16}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm leading-relaxed font-serif"
                    data-testid="rewrite-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {rewriteState.editedDraft?.split(/\s+/).filter(Boolean).length || 0} words ·{' '}
                    {rewriteState.editedDraft?.length || 0} chars
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-2 border-t">
                  <button
                    onClick={onSave}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-black text-white text-xs uppercase tracking-[0.2em] hover:bg-neutral-800"
                    data-testid="rewrite-save"
                  >
                    <Save className="w-4 h-4" /> Save to artwork
                  </button>
                  <button
                    onClick={onRegenerate}
                    className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 text-xs uppercase tracking-[0.2em] hover:bg-gray-50"
                    data-testid="rewrite-regenerate"
                  >
                    <Wand2 className="w-4 h-4" /> Regenerate
                  </button>
                  <button
                    onClick={onClose}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-gray-600 hover:text-black ml-auto"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default AIRewriteModal;
